import { faPlayCircle } from '@fortawesome/pro-light-svg-icons/faPlayCircle';
import { faPrint } from '@fortawesome/pro-light-svg-icons/faPrint';
import { Property } from '@propertypal/shared/src/types/property';
import DropButton from '@propertypal/web-ui/src/buttons/DropButton';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { SubHeading } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import config from '../../../../../app.config.json';
import {
  ButtonsRow,
  Container,
  Content,
  DropButtonWrapper,
  DropOptions,
  IconWrapper,
  ItemRow,
} from './PropertyPdfBar.style';

interface Props {
  property: Property;
  onItemClick: (nextUrl: string) => void;
  header?: string;
}

const MEDIA_SERVER =
  config.env === 'Develop' ? 'https://public-media-api-dev-v4ua7muuea-nw.a.run.app' : 'https://media.propertypal.com';

const PropertyPdfBar: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const videoTitle = `Watch The ${props.property.type === 'development' ? 'Development' : 'Property'} Video`;
  const [showPdfFiles, setShowPdfFiles] = useState<boolean>(false);

  if (
    !props.property.videos?.length &&
    !props.property['360Tours']?.length &&
    !props.property.brochures?.length &&
    props.property.type === 'development'
  ) {
    return null;
  }

  return (
    <Container className="hide-print" data-testid="pp-pdf-bar">
      {props.header && <SubHeading>{props.header}</SubHeading>}
      <Content>
        <ButtonsRow>
          {!!props.property.videos?.length && (
            <ItemRow as="button" onClick={() => props.onItemClick('/video')} data-testid="watchVideoButton">
              <IconWrapper>
                <FontAwesomeIcon icon={faPlayCircle} color={theme.textDark} style={{ fontSize: 24 }} />
              </IconWrapper>
              <p className="pp-primary-text">{videoTitle}</p>
            </ItemRow>
          )}

          {props.property['360Tours'] &&
            props.property['360Tours'].map((tour) => (
              <ItemRow
                key={tour.displayOrder}
                as="button"
                onClick={() => props.onItemClick(`/tour${tour.displayOrder}`)}
                data-testid="viewTourButton"
              >
                <IconWrapper>
                  <FontAwesomeIcon icon={faPlayCircle} color={theme.textDark} style={{ fontSize: 24 }} />
                </IconWrapper>

                <p className="pp-primary-text">{tour.type === 'video' ? videoTitle : 'Take The 3D Tour'}</p>
              </ItemRow>
            ))}

          {props.property.type === 'property' && (
            <ItemRow href={`/property-print?property=${props.property.id}`} target="_blank">
              <IconWrapper>
                <FontAwesomeIcon icon={faPrint} color={theme.textDark} style={{ fontSize: 24 }} />
              </IconWrapper>

              <p className="pp-primary-text">Printer Friendly Version</p>
            </ItemRow>
          )}
        </ButtonsRow>

        {!!props.property.brochures?.length && (
          <DropButtonWrapper>
            <DropButton
              containerStyle={{ borderRadius: 6 }}
              textStyle={{ fontSize: 16 }}
              text="Download More Information"
              active={showPdfFiles}
              onClick={() => setShowPdfFiles(!showPdfFiles)}
              testID="downloadBrochureButton"
            />

            {showPdfFiles && (
              <DropOptions>
                {props.property.brochures.map((brochure) => (
                  <a key={brochure.url} href={brochure.url} download={brochure.title} type="button">
                    {brochure.title} ({brochure.sizeDescription})
                  </a>
                ))}

                {props.property.brochures.length > 1 && (
                  <a
                    href={`${MEDIA_SERVER}/f/${props.property.type === 'development' ? 'd' : 'p'}/${props.property.id}`}
                  >
                    Download All
                  </a>
                )}
              </DropOptions>
            )}
          </DropButtonWrapper>
        )}
      </Content>
    </Container>
  );
};

export default PropertyPdfBar;
